import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  Col,
  UiBox,
  UiButton,
  UiFormControl,
  UiFormErrorMessage,
  UiImage,
  UiQuestionnaireLayout,
  UiSimpleGrid,
  UiText,
  UiTextarea,
} from '~/components/elements';
import {ScrollToBottomButton} from '~/components/shared/ScrollToBottomButton';
import {EMERGENCY_CONTRACEPTION_KEYS} from '~/constants/medical-questionnaires';
import {useInput, useNotification} from '~/hooks';
import {setNextQuestionnaire, setQuestionnaireAnswer} from '~/redux/questionnaires/slice';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import {spacing} from '~/utils/theme/spacing';

const Questionnaire12Screen = () => {
  const dispatch: AppDispatch = useDispatch();
  const {addMessage, addValidationMessage} = useNotification(dispatch);

  const currentAnswer = useSelector(
    (state: RootState) => state.questionnaires?.collectData[EMERGENCY_CONTRACEPTION_KEYS.NEGATIVE_BIRTH_CONTROL]?.data,
  );

  const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);

  const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = questionnaires?.find(i => i.key === EMERGENCY_CONTRACEPTION_KEYS.NEGATIVE_BIRTH_CONTROL);

  const rules = {
    medicationsTaking: (attributes: any) => {
      return {
        presence: {allowEmpty: false, message: `^This field can't be blank`},
        length: {
          minimum: 5,
          tooShort: '^This field is too short (minimum is %{count} characters)',
          tokenizer: function (value: any) {
            return trimSpaces(value);
          },
        },
      };
    },
  };
  const {inputs, setInputs, validation, trimSpacesOnChange, trimSpaces} = useInput(
    {medicationsTaking: currentAnswer?.medicationsTaking || '', selectedOption: currentAnswer?.selectedOption || ''},
    {rules},
  );

  const renderQnsMediacation = () => {
    if (inputs.selectedOption === 'YES') {
      return (
        <UiBox mt={spacing.medium} className="animate__animated animate__fadeInLeft">
          <UiText fontSize="md" fontWeight="bold">
            {questionnaire?.questions?.sub}
          </UiText>
          <UiFormControl id="medicationsTaking" isRequired isInvalid={!!validation?.errors?.medicationsTaking}>
            <UiTextarea
              mt={spacing.small}
              bg="white"
              borderRadius={spacing.tiny}
              placeholder="Type your response here..."
              value={inputs.medicationsTaking}
              onChange={({target}) => setInputs({medicationsTaking: target.value})}
              minH="200px"
            />
            {validation?.errors?.medicationsTaking && (
              <UiFormErrorMessage>{validation?.errors?.medicationsTaking[0]}</UiFormErrorMessage>
            )}
          </UiFormControl>
        </UiBox>
      );
    }
    return null;
  };

  const onClickSubmit = () => {
    if (inputs.selectedOption === 'YES') {
      const errors = validation.getErrors();
      if (errors) {
        return;
      }
    }

    dispatch(
      setQuestionnaireAnswer({
        key: EMERGENCY_CONTRACEPTION_KEYS.NEGATIVE_BIRTH_CONTROL,
        index: questionnaire?.index || '',
        data: inputs,
      }),
    );
    dispatch(setNextQuestionnaire({value: EMERGENCY_CONTRACEPTION_KEYS.OTHER_MEDICAL_HISTORY}));
  };

  return (
    <UiQuestionnaireLayout
      handleOnNext={onClickSubmit}
      actions={questionnaire?.actions}
      disableNext={!inputs.selectedOption}
      checkoutStep={questionnaire?.checkoutStep}
    >
      <Col alignItems="center" textAlign="center">
        <UiImage type="icon" name={questionnaire?.icon} boxSize={spacing.mLarge} mt={spacing.medium} />

        <UiText fontSize="xl" fontWeight="bold" mt={spacing.large}>
          {questionnaire?.questions?.default}
        </UiText>

        <UiBox mt={spacing.small} w="100%">
          <UiSimpleGrid columns={2} spacing={5}>
            {questionnaire?.options?.map(value => (
              <ScrollToBottomButton
                key={`${value.label}-${value.value}`}
                title={value.label}
                variant={inputs.selectedOption === value.value ? 'solid' : 'toggle'}
                onClick={() => setInputs({selectedOption: value.value})}
              />
            ))}
          </UiSimpleGrid>
          {renderQnsMediacation()}
        </UiBox>
      </Col>
    </UiQuestionnaireLayout>
  );
};

export default Questionnaire12Screen;
